import React, { useState, useEffect, useRef, useCallback } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import { Card, Modal, ModalBody } from "reactstrap";
import { Link } from "react-router-dom";
import {
  Table,
  Empty,
  Badge,
  Select,
  Input,
  Checkbox,
  Descriptions,
  Tag,
  Statistic,
  Card as AntCard,
  Drawer,
  Badge as AntBadge,
  Tooltip,
} from "antd";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import {
  CalculatorOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  TagOutlined,
} from "@ant-design/icons";
import moment from "moment";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const taxTypeOptions = [
  { label: "INDIRECT", value: "INDIRECT" },
  { label: "TDS", value: "TDS" },
  { label: "TCS", value: "TCS" },
  { label: "CESS", value: "CESS" },
];

const taxTypeColor: Record<string, string> = {
  INDIRECT: "blue",
  TDS: "orange",
  TCS: "purple",
  CESS: "cyan",
};

const statusColor: Record<string, string> = {
  ACTIVE: "success",
  INACTIVE: "danger",
};

const TaxList: React.FC = () => {
  const [onSearchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(onSearchText, 300);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
    sortOrder: "DESC" as "ASC" | "DESC",
    direction: "P" as "P" | "F" | "B",
    pageDiff: 0,
    paginationId: null as number | null,
  });

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [filterTaxType, setFilterTaxType] = useState<string | undefined>(undefined);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Modal states
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedTax, setSelectedTax] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  // Form states (shared for create/edit)
  const [formTaxName, setFormTaxName] = useState("");
  const [formTaxRate, setFormTaxRate] = useState("");
  const [formTaxType, setFormTaxType] = useState("INDIRECT");
  const [formIsDefault, setFormIsDefault] = useState(false);
  const [formStatus, setFormStatus] = useState("ACTIVE");

  const buildFilters = useCallback(() => {
    const filters: any = {};
    if (filterStatus) filters.status = filterStatus;
    if (filterTaxType) filters.taxType = filterTaxType;
    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [filterStatus, filterTaxType]);

  const fetchData = async (params: any = {}) => {
    setLoading(true);
    try {
      const filters = params.filters !== undefined ? params.filters : buildFilters();
      const apiParams: any = {
        sortOrder: params.sortOrder || pagination.sortOrder,
        direction: params.direction || pagination.direction,
        pageSize: params.pageSize || pagination.pageSize,
        pageDiff: params.pageDiff ?? pagination.pageDiff,
        paginationId: params.paginationId ?? pagination.paginationId,
        searchQuery: params.searchQuery !== undefined ? params.searchQuery : onSearchText,
      };

      if (filters) {
        if (filters.status) apiParams["filters[status]"] = filters.status;
        if (filters.taxType) apiParams["filters[taxType]"] = filters.taxType;
      }

      const { data, status, message } = await superAdminService.getTaxList(apiParams);

      if (status) {
        const list = Array.isArray(data) ? data : data?.list || [];
        const count = Array.isArray(data) ? data.length : data?.count || 0;
        setDataSource(list);
        setPagination((prev) => ({
          ...prev,
          total: count,
          ...(params.current && { current: params.current }),
          ...(params.pageSize && { pageSize: params.pageSize }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
          ...(params.direction && { direction: params.direction }),
          ...(params.pageDiff !== undefined && { pageDiff: params.pageDiff }),
          ...(params.paginationId !== undefined && { paginationId: params.paginationId }),
        }));
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
  }, []);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P", searchQuery: debouncedSearch.trim() });
  }, [debouncedSearch]);

  const tableOnChange = async (newPagination: any) => {
    const apiParams: any = {
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    };

    if (newPagination.pageSize !== pagination.pageSize) {
      apiParams.pageDiff = Math.floor(((pagination.current - 1) * pagination.pageSize) / newPagination.pageSize);
    } else if (newPagination.current !== pagination.current) {
      apiParams.direction = newPagination.current > pagination.current ? "F" : "B";
      apiParams.pageDiff = Math.abs(newPagination.current - pagination.current) - 1;
      if (apiParams.direction === "F" && dataSource.length > 0) {
        apiParams.paginationId = dataSource[dataSource.length - 1]?.paginationId || dataSource[dataSource.length - 1]?.taxId;
      } else if (apiParams.direction === "B" && dataSource.length > 0) {
        apiParams.paginationId = dataSource[0]?.paginationId || dataSource[0]?.taxId;
      }
    }

    await fetchData(apiParams);
  };

  const handleClearFilters = () => {
    setFilterStatus(undefined);
    setFilterTaxType(undefined);
  };

  const filtersInitialized = useRef(false);
  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }
    fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
  }, [filterStatus, filterTaxType]);

  // --- Create Modal ---
  const openCreateModal = () => {
    setFormTaxName("");
    setFormTaxRate("");
    setFormTaxType("INDIRECT");
    setFormIsDefault(false);
    setCreateModal(true);
  };

  const handleCreate = async () => {
    if (!formTaxName.trim()) return;
    setSaving(true);
    try {
      const payload = {
        taxName: formTaxName.trim(),
        taxRate: parseFloat(formTaxRate),
        taxType: formTaxType,
        isDefault: formIsDefault,
      };
      const res = await superAdminService.createTax(payload);
      if (res.status) {
        Swal.fire("Success", "Tax created successfully", "success");
        setCreateModal(false);
        hasFetched.current = false;
        fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // --- View Modal ---
  const openViewModal = (record: any) => {
    setSelectedTax(record);
    setViewModal(true);
  };

  // --- Edit Modal ---
  const openEditModal = (record: any) => {
    setSelectedTax(record);
    setFormTaxName(record.taxName);
    setFormTaxRate(String(record.taxRate));
    setFormTaxType(record.taxType);
    setFormIsDefault(record.isDefault === true || record.isDefault === 1);
    setFormStatus(record.status);
    setEditModal(true);
  };

  const handleUpdate = async () => {
    if (!selectedTax) return;
    setSaving(true);
    try {
      const payload: any = { taxId: Number(selectedTax.taxId) };
      if (formTaxName !== selectedTax.taxName) payload.taxName = formTaxName.trim();
      if (parseFloat(formTaxRate) !== Number(selectedTax.taxRate)) payload.taxRate = parseFloat(formTaxRate);
      if (formTaxType !== selectedTax.taxType) payload.taxType = formTaxType;
      if (formIsDefault !== (selectedTax.isDefault === true || selectedTax.isDefault === 1)) payload.isDefault = formIsDefault;
      if (formStatus !== selectedTax.status) payload.status = formStatus;

      const res = await superAdminService.updateTax(payload);
      if (res.status) {
        Swal.fire("Success", "Tax updated successfully", "success");
        setEditModal(false);
        hasFetched.current = false;
        fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // --- Delete ---
  const handleDelete = async (record: any) => {
    const result = await Swal.fire({
      title: "Delete Tax?",
      text: `Are you sure you want to delete "${record.taxName}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      confirmButtonColor: "#d33",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await superAdminService.deleteTax(record.taxId);
      if (res.status) {
        Swal.fire("Deleted!", "Tax deleted successfully", "success");
        hasFetched.current = false;
        fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    }
  };

  const columns = [
    {
      title: "Tax Name",
      dataIndex: "taxName",
      key: "taxName",
      width: 200,
      render: (text: string, record: any) => (
        <a
          href="#view"
          className="text-primary fw-bold"
          onClick={(e) => {
            e.preventDefault();
            openViewModal(record);
          }}
        >
          {text || "-"}
        </a>
      ),
    },
    {
      title: "Tax Rate",
      dataIndex: "taxRate",
      key: "taxRate",
      width: 120,
      render: (val: number) => <span style={{ fontWeight: 600 }}>{Number(val).toFixed(2)}%</span>,
    },
    {
      title: "Tax Type",
      dataIndex: "taxType",
      key: "taxType",
      width: 130,
      render: (type: string) => <Tag color={taxTypeColor[type] || "default"}>{type}</Tag>,
    },
    {
      title: "Default",
      dataIndex: "isDefault",
      key: "isDefault",
      width: 80,
      render: (val: any) => (val === true || val === 1) ? <Tag color="green">Yes</Tag> : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <span className={`tb-status text-${statusColor[status] || "secondary"}`}>{status}</span>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 130,
      render: (date: string) => date ? moment(date).format("DD MMM YYYY") : "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, record: any) => (
        <div className="d-flex" style={{ gap: 4 }}>
          <a
            href="#view"
            className="btn btn-sm btn-icon btn-trigger"
            title="View"
            onClick={(e) => { e.preventDefault(); openViewModal(record); }}
          >
            <Icon name="eye" />
          </a>
          <a
            href="#edit"
            className="btn btn-sm btn-icon btn-trigger"
            title="Edit"
            onClick={(e) => { e.preventDefault(); openEditModal(record); }}
          >
            <Icon name="edit" />
          </a>
          <a
            href="#delete"
            className="btn btn-sm btn-icon btn-trigger text-danger"
            title="Delete"
            onClick={(e) => { e.preventDefault(); handleDelete(record); }}
          >
            <Icon name="trash" />
          </a>
        </div>
      ),
    },
  ];

  const hasActiveFilters = filterStatus || filterTaxType;
  const activeFilterCount = [filterStatus, filterTaxType].filter(Boolean).length;

  return (
    <React.Fragment>
      <Head title="Tax Management" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Tax Management
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>You have total {pagination.total} taxes.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button color="primary" onClick={openCreateModal}>
                <Icon name="plus"></Icon>
                <span>Add Tax</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            <div className="card-inner py-3">
              <div className="d-flex justify-content-between align-items-center">
                <Input
                  prefix={<SearchOutlined style={{ color: "#b0b0b0" }} />}
                  placeholder="Search by tax name..."
                  value={onSearchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  variant="borderless"
                  style={{ width: 570, borderBottom: "1.5px solid #e0e0e0", borderRadius: 0 }}
                  allowClear
                />
                <div className="d-flex align-items-center" style={{ gap: 4 }}>
                  <Tooltip title="Refresh">
                    <span
                      onClick={() => {
                        hasFetched.current = false;
                        fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
                      }}
                      style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 36, height: 36, borderRadius: "50%", cursor: "pointer",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <ReloadOutlined style={{ fontSize: 16, color: "#555" }} />
                    </span>
                  </Tooltip>
                  <Tooltip title="Filters">
                    <AntBadge count={activeFilterCount} size="small" offset={[-4, 4]}>
                      <span
                        onClick={() => setFilterDrawerOpen(true)}
                        style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          width: 36, height: 36, borderRadius: "50%", cursor: "pointer",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <FilterOutlined style={{ fontSize: 16, color: "#555" }} />
                      </span>
                    </AntBadge>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div className="card-inner p-0">
              {loading ? (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                      {["Tax Name", "Tax Rate", "Tax Type", "Default", "Status", "Created", "Actions"].map((h) => (
                        <th key={h} style={{ padding: "11px 12px", fontSize: 12, fontWeight: 600, color: "#8c8c8c", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(10)].map((_, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 140, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 60, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 70, height: 22, borderRadius: 4 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 35, height: 22, borderRadius: 4 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 55, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 85, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 70, height: 18, borderRadius: 4 }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : dataSource.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  rowKey="taxId"
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '15', '20', '50', '100', '500'],
                  }}
                  onChange={tableOnChange}
                  scroll={{ x: 800  }}
                  bordered
                  size="small"
                />
              ) : (
                <Empty description="No taxes found" />
              )}
            </div>
          </Card>
        </Block>

        {/* Create Tax Modal */}
        <Modal isOpen={createModal} toggle={() => !saving && setCreateModal(false)} size="md">
          <ModalBody>
            <h5 className="title mb-4">
              <PlusOutlined style={{ marginRight: 8 }} />
              Create Tax
            </h5>
            <div className="form-group mb-3">
              <label className="form-label">Tax Name <span className="text-danger">*</span></label>
              <Input
                value={formTaxName}
                onChange={(e) => setFormTaxName(e.target.value)}
                maxLength={50}
                placeholder="e.g., GST, IGST, CGST"
              />
            </div>
            <div className="form-group mb-3">
              <label className="form-label">Tax Rate (%) <span className="text-danger">*</span></label>
              <Input
                type="number"
                value={formTaxRate}
                onChange={(e) => setFormTaxRate(e.target.value)}
                min={0}
                max={100}
                step={0.01}
                placeholder="e.g., 18.00"
              />
            </div>
            <div className="form-group mb-3">
              <label className="form-label">Tax Type <span className="text-danger">*</span></label>
              <Select
                style={{ width: "100%" }}
                value={formTaxType}
                onChange={(val) => setFormTaxType(val)}
                options={taxTypeOptions}
                size="large"
              />
            </div>
            <div className="form-group mb-3">
              <Checkbox checked={formIsDefault} onChange={(e) => setFormIsDefault(e.target.checked)}>
                Set as Default Tax
              </Checkbox>
            </div>
            <div className="mt-4" style={{ display: "flex", gap: 8 }}>
              <Button
                color="primary"
                size="sm"
                onClick={handleCreate}
                disabled={saving || !formTaxName.trim() || !formTaxRate}
              >
                {saving ? <LoadingSpinner inline size="sm" /> : "Create Tax"}
              </Button>
              <Button color="secondary" size="sm" onClick={() => setCreateModal(false)} disabled={saving}>
                Cancel
              </Button>
            </div>
          </ModalBody>
        </Modal>

        {/* Edit Tax Modal */}
        <Modal isOpen={editModal} toggle={() => !saving && setEditModal(false)} size="md">
          <ModalBody>
            <h5 className="title mb-4">
              <EditOutlined style={{ marginRight: 8 }} />
              Edit Tax
            </h5>
            <div className="form-group mb-3">
              <label className="form-label">Tax Name</label>
              <Input value={formTaxName} onChange={(e) => setFormTaxName(e.target.value)} maxLength={50} />
            </div>
            <div className="form-group mb-3">
              <label className="form-label">Tax Rate (%)</label>
              <Input
                type="number"
                value={formTaxRate}
                onChange={(e) => setFormTaxRate(e.target.value)}
                min={0}
                max={100}
                step={0.01}
              />
            </div>
            <div className="form-group mb-3">
              <label className="form-label">Tax Type</label>
              <Select
                style={{ width: "100%" }}
                value={formTaxType}
                onChange={(val) => setFormTaxType(val)}
                options={taxTypeOptions}
                size="large"
              />
            </div>
            <div className="form-group mb-3">
              <label className="form-label">Status</label>
              <Select
                style={{ width: "100%" }}
                value={formStatus}
                onChange={(val) => setFormStatus(val)}
                options={[
                  { label: "ACTIVE", value: "ACTIVE" },
                  { label: "INACTIVE", value: "INACTIVE" },
                ]}
                size="large"
              />
            </div>
            <div className="form-group mb-3">
              <Checkbox checked={formIsDefault} onChange={(e) => setFormIsDefault(e.target.checked)}>
                Set as Default Tax
              </Checkbox>
            </div>
            <div className="mt-4" style={{ display: "flex", gap: 8 }}>
              <Button color="primary" size="sm" onClick={handleUpdate} disabled={saving || !formTaxName.trim()}>
                {saving ? <LoadingSpinner inline size="sm" /> : "Save Changes"}
              </Button>
              <Button color="secondary" size="sm" onClick={() => setEditModal(false)} disabled={saving}>
                Cancel
              </Button>
            </div>
          </ModalBody>
        </Modal>

        {/* View Tax Modal */}
        <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg">
          <ModalBody>
            {selectedTax && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="title mb-0">
                    <EyeOutlined style={{ marginRight: 8 }} />
                    Tax Details
                  </h5>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button
                      color="primary"
                      size="sm"
                      className="btn-dim"
                      onClick={() => {
                        setViewModal(false);
                        openEditModal(selectedTax);
                      }}
                    >
                      <Icon name="edit" /> <span>Edit</span>
                    </Button>
                    <Button
                      color="danger"
                      size="sm"
                      className="btn-dim"
                      onClick={() => {
                        setViewModal(false);
                        handleDelete(selectedTax);
                      }}
                    >
                      <Icon name="trash" /> <span>Delete</span>
                    </Button>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <AntCard size="small">
                      <Statistic
                        title="Tax Rate"
                        value={Number(selectedTax.taxRate).toFixed(2)}
                        suffix="%"
                        valueStyle={{ color: "#1890ff", fontSize: 28 }}
                        prefix={<CalculatorOutlined />}
                      />
                    </AntCard>
                  </div>
                  <div className="col-md-4">
                    <AntCard size="small">
                      <Statistic
                        title="Tax Type"
                        value={selectedTax.taxType}
                        valueStyle={{ fontSize: 20 }}
                      />
                    </AntCard>
                  </div>
                  <div className="col-md-4">
                    <AntCard size="small">
                      <Statistic
                        title="Status"
                        value={selectedTax.status}
                        valueStyle={{
                          fontSize: 20,
                          color: selectedTax.status === "ACTIVE" ? "#52c41a" : "#ff4d4f",
                        }}
                      />
                    </AntCard>
                  </div>
                </div>

                <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
                  <Descriptions.Item label="Tax ID">{selectedTax.taxId}</Descriptions.Item>
                  <Descriptions.Item label="Tax Name">
                    <span style={{ fontWeight: 600 }}>{selectedTax.taxName}</span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Tax Rate">
                    <Tag color="blue" style={{ fontSize: 14 }}>{Number(selectedTax.taxRate).toFixed(2)}%</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Tax Type">
                    <Tag color={taxTypeColor[selectedTax.taxType] || "default"}>{selectedTax.taxType}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={selectedTax.status === "ACTIVE" ? "green" : "red"}>{selectedTax.status}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Default">
                    {(selectedTax.isDefault === true || selectedTax.isDefault === 1) ? (
                      <Tag color="green">Yes</Tag>
                    ) : (
                      <span className="text-soft">No</span>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created">
                    {selectedTax.createdDate ? moment(selectedTax.createdDate).format("DD MMM YYYY, hh:mm A") : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Last Modified">
                    {selectedTax.modifiedDate ? moment(selectedTax.modifiedDate).format("DD MMM YYYY, hh:mm A") : "-"}
                  </Descriptions.Item>
                </Descriptions>

                <div className="mt-4 text-end">
                  <Button color="secondary" size="sm" onClick={() => setViewModal(false)}>
                    Close
                  </Button>
                </div>
              </>
            )}
          </ModalBody>
        </Modal>
      </Content>

      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FilterOutlined style={{ fontSize: 16, color: "#6576ff" }} />
            <span style={{ fontSize: 16, fontWeight: 700 }}>Filters</span>
            {activeFilterCount > 0 && (
              <AntBadge count={activeFilterCount} style={{ backgroundColor: "#6576ff", marginLeft: 4 }} />
            )}
          </div>
        }
        placement="right"
        onClose={() => setFilterDrawerOpen(false)}
        open={filterDrawerOpen}
        width={450}
        zIndex={1060}
        extra={
          hasActiveFilters ? (
            <span style={{ fontSize: 13, color: "#ff4d4f", cursor: "pointer", fontWeight: 600 }} onClick={handleClearFilters}>
              Reset All
            </span>
          ) : null
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <div style={{
            padding: "14px 16px", borderRadius: 10,
            background: "linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)",
            border: "1px solid #d6e4ff",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <CheckCircleOutlined style={{ color: "#1890ff", fontSize: 15 }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Status</span>
            </div>
            <Select
              placeholder="All statuses"
              allowClear
              style={{ width: "100%" }}
              value={filterStatus}
              onChange={(val) => setFilterStatus(val)}
              options={[
                { label: "ACTIVE", value: "ACTIVE" },
                { label: "INACTIVE", value: "INACTIVE" },
              ]}
            />
          </div>

          <div style={{
            padding: "14px 16px", borderRadius: 10,
            background: "linear-gradient(135deg, #f6ffed 0%, #fcffe6 100%)",
            border: "1px solid #d9f7be",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <TagOutlined style={{ color: "#52c41a", fontSize: 15 }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Tax Type</span>
            </div>
            <Select
              placeholder="All types"
              allowClear
              style={{ width: "100%" }}
              value={filterTaxType}
              onChange={(val) => setFilterTaxType(val)}
              options={taxTypeOptions}
            />
          </div>
        </div>
      </Drawer>
    </React.Fragment>
  );
};

export default TaxList;
